import React from "react";
import { Select } from "@mantine/core";

/**
 * Gibberish check level values matching backend GIBBERISH_CHECK_LEVELS
 */
export const GIBBERISH_CHECK_LEVELS = {
  NONE: "none",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

export type GibberishCheckLevel =
  (typeof GIBBERISH_CHECK_LEVELS)[keyof typeof GIBBERISH_CHECK_LEVELS];

/**
 * Options for form-level selector (no "inherit" option)
 */
const FORM_LEVEL_OPTIONS = [
  {
    value: GIBBERISH_CHECK_LEVELS.NONE,
    label: "None - Disabled",
  },
  {
    value: GIBBERISH_CHECK_LEVELS.LOW,
    label: "Low - Catch obvious garbage only",
  },
  {
    value: GIBBERISH_CHECK_LEVELS.MEDIUM,
    label: "Medium - Balanced detection (recommended)",
  },
  {
    value: GIBBERISH_CHECK_LEVELS.HIGH,
    label: "High - Strict, may have false positives",
  },
];

/**
 * Options for field-level selector (includes "use form setting" option)
 */
const FIELD_LEVEL_OPTIONS = [
  {
    value: "",
    label: "Use form setting",
  },
  ...FORM_LEVEL_OPTIONS,
];

export interface GibberishCheckSelectorProps {
  /** Selected level value */
  value: string | null | undefined;

  /** Callback when selection changes */
  onChange?: (level: string | null) => void;

  /** Field label */
  label?: string;

  /** Field description */
  description?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /**
   * Whether this is a field-level selector.
   * If true, shows "Use form setting" option and allows null value.
   * If false (default), shows only the level options for form-level config.
   */
  isFieldLevel?: boolean;
}

export const GibberishCheckSelector: React.FC<GibberishCheckSelectorProps> = ({
  value,
  onChange,
  label = "Gibberish Detection",
  description,
  placeholder = "Select detection level",
  required = false,
  error,
  disabled = false,
  size = "md",
  isFieldLevel = false,
}) => {
  const options = isFieldLevel ? FIELD_LEVEL_OPTIONS : FORM_LEVEL_OPTIONS;

  // Normalize value for Select component
  // For field-level, null/undefined means "use form setting" (empty string in Select)
  // For form-level, null/undefined defaults to "medium"
  const normalizedValue = isFieldLevel
    ? value ?? ""
    : value ?? GIBBERISH_CHECK_LEVELS.MEDIUM;

  const handleChange = (selectedValue: string | null) => {
    if (isFieldLevel) {
      // For field-level, empty string means null (use form setting)
      onChange?.(selectedValue === "" ? null : selectedValue);
    } else {
      // For form-level, always pass a valid level
      onChange?.(selectedValue ?? GIBBERISH_CHECK_LEVELS.MEDIUM);
    }
  };

  return (
    <Select
      label={label}
      description={description}
      placeholder={placeholder}
      data={options}
      value={normalizedValue}
      onChange={handleChange}
      required={required}
      withAsterisk={required}
      clearable={false}
      disabled={disabled}
      error={error}
      size={size}
    />
  );
};
